import { bff } from '@/lib/http/bff';

// ── Scope-based device entity types ────────────────────────────────────────

export interface Device {
  device_id: string;
  id_scope: number;
  label?: string | null;
  is_active: boolean;
  /** 'device_code' for Tier 2 (RFC 8628) devices; null/undefined for Tier 1 (IoT). */
  authorized_via?: string | null;
  /** 'fe-peer' | 'pacdaemon' | 'iot' — resolved from the registration source. */
  device_type?: string | null;
  /** Browser / daemon user-agent string captured at device-code request time. */
  user_agent?: string | null;
  /** Origin URL the device connected from (e.g. https://app.example.com). */
  origin?: string | null;
  /** OS / runtime platform (e.g. 'Win32', 'linux', 'darwin'). */
  platform?: string | null;
  /** Browser language (e.g. 'en-US'). */
  language?: string | null;
  /** Screen resolution (e.g. '1920x1080'). */
  screen?: string | null;
  /** Remote IP at device-code request time. */
  remote_ip?: string | null;
  /** User ID of the admin who approved this device. */
  approved_by?: string | null;
  /** ISO timestamp when the device was approved. */
  approved_at?: string | null;
  /** Comma-separated list of scopes granted. */
  scope_granted?: string | null;
  /** ISO timestamp of the last heartbeat / activity. */
  last_seen_at?: string | null;
  /** Browser fingerprint hash for stable device identity across sessions. */
  fingerprint?: string | null;
  created_at?: string;
}

export interface DeviceConfig {
  id_scope: number;
  device_id: string;
  revision: number;
  is_active: boolean;
  config: Record<string, any>;
  sha256?: string;
  created_at?: string;
}

export interface DeviceWithConfig extends Device {
  current_config: DeviceConfig | null;
}

// ── Device CRUD ────────────────────────────────────────────────────────────

export const fetchDevicesApi = async (): Promise<Device[]> => {
  const res = await bff.get<{ items: Device[] }>('/device-ops/devices');
  return Array.isArray(res?.items) ? res.items : [];
};

export type RegisterDeviceRequest = {
  device_id: string;
  label?: string | null;
  is_active?: boolean;
};

export const registerDeviceApi = (payload: RegisterDeviceRequest) =>
  bff.post('/device-ops/devices', payload);

export const fetchDeviceWithConfigApi = async (deviceId: string): Promise<DeviceWithConfig | null> => {
  const id = encodeURIComponent(deviceId);
  return bff.get<DeviceWithConfig>(`/device-ops/devices/${id}`);
};

// ── Config CRUD ────────────────────────────────────────────────────────────

export const fetchDeviceConfigApi = async (deviceId: string): Promise<DeviceConfig | null> => {
  const id = encodeURIComponent(deviceId);
  return bff.get<DeviceConfig>(`/device-ops/devices/${id}/config`);
};

export type WriteDeviceConfigRequest = {
  config: Record<string, any>;
  is_active?: boolean;
};

export const writeDeviceConfigApi = (
  deviceId: string,
  payload: WriteDeviceConfigRequest,
) => {
  const id = encodeURIComponent(deviceId);
  return bff.post(`/device-ops/devices/${id}/config`, payload);
};

// ── Device lifecycle ────────────────────────────────────────────────────────

export const revokeDeviceApi = async (
  deviceId: string,
): Promise<{ status: string; device_id: string; scope_updated: boolean }> => {
  const id = encodeURIComponent(deviceId);
  return bff.post(`/device-ops/devices/${id}/revoke`);
};

export const blacklistDeviceApi = async (
  deviceId: string,
  reason?: string,
): Promise<{ status: string; device_id: string; scope_updated: boolean; blacklist: unknown }> => {
  const id = encodeURIComponent(deviceId);
  return bff.post(`/device-ops/devices/${id}/blacklist`, {
    ...(reason ? { reason } : {}),
  });
};

// ── Compat / derived helpers ───────────────────────────────────────────────
// Used by WorkflowBuilder to list available {{device.*}} binding keys.

export const fetchDeviceConfigKeysApi = async (): Promise<string[]> => {
  const devices = await fetchDevicesApi();
  if (!devices.length) return [];
  // Fetch the first active device's config to derive available keys
  const active = devices.find((d) => d.is_active) ?? devices[0];
  const cfg = await fetchDeviceConfigApi(active.device_id);
  if (!cfg?.config || typeof cfg.config !== 'object') return [];
  return Object.keys(cfg.config)
    .filter((k) => typeof k === 'string' && k.trim() !== '')
    .sort();
};
